import { isArray } from "lodash-es";

import type { CardCreateProps } from "@/action/addCard";
import { cardPosStep } from "@/action/addCard";
import { cardActions } from "@/action/cardActions";
import { selectionActions } from "@/action/selectionActions";
import { windowFromRelative } from "@/math/coordinate-transform";
import { relativeFromViewport } from "@/math/coordinate-utils";
import { minus, plus } from "@/math/coordinates";
import type { AlmSourceId } from "@/model/baseTypes";
import type { BoardCard } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { useBoardStore } from "@/store/board";
import { useModalStore } from "@/store/modal";
import { useZoomStore } from "@/store/zoom";
import { centerCoord, viewportFromWindow } from "@/utils/dom/coordinates";
import { eventTarget, isInput, lineSeparator } from "@/utils/dom/dom";
import { findFreePosition } from "@/utils/layout";
import { registerGlobalShortcut } from "@/utils/shortcut/shortcuts";
import { isNonPrimitive } from "@/utils/type";

interface EnhancedClipboardEvent extends ClipboardEvent {
  text: string;
}

function getClipboardText(e: ClipboardEvent) {
  return (e as EnhancedClipboardEvent).text;
}

export function setClipboardText(e: ClipboardEvent, text: string) {
  (e as EnhancedClipboardEvent).text = text;
}

export function registerCopyPaste() {
  registerGlobalShortcut(cardActions.duplicate, { prevent: true });

  document.addEventListener("paste", (e) => {
    if (cardPasteTarget(eventTarget(e))) {
      const data = parseJson(e.clipboardData?.getData("text/sticky"));
      if (isArray(data) && data.every(isSerializedCard)) {
        // Calculate the 'center' of the pasted stickies
        // When zoomed in, paste the stickies in the center of the window
        const pasteInCenter = useZoomStore().factor >= 1.2;
        const initialPosition = pasteInCenter ? relativeFromViewport(centerCoord()) : data[0].pos;
        const freePos = findFreePosition(initialPosition, cardPosStep());
        selectionActions.paste(
          "keyboard-shortcut",
          data.map((d) => deserializeCard(data[0].pos, d, freePos)),
        );
      } else {
        const text = e.clipboardData?.getData("text");
        if (text) {
          void cardActions.pasteText("keyboard-shortcut", { text, pos: "auto" });
        }
      }
    }
  });

  document.addEventListener("copy", (e) => {
    const cards = useBoardStore().selectedOrActiveStickies;
    if (cards.length > 0 && e.clipboardData) {
      cardActions.copy("keyboard-shortcut");
      const text = selectedText(e);
      if (cards.length === 1 && text) {
        setClipboard(e.clipboardData, [cardWithText(cards[0], text)]);
      } else {
        setClipboard(e.clipboardData, cards);
      }
      e.preventDefault();
    }
  });
}

function cardWithText(card: BoardCard, text: string) {
  return { ...card, data: { ...card.data, text } };
}

function setClipboard(clipboard: DataTransfer, cards: BoardCard[]) {
  clipboard.setData("text/plain", cards.map((card) => card.data.text).join(lineSeparator));
  clipboard.setData("text/sticky", JSON.stringify(cards.map(serializeCard)));
}

function cardPasteTarget(e: HTMLElement) {
  return !useModalStore().isModalOpen() && !isInput(e);
}

function selectedText(ev: ClipboardEvent) {
  const text = getClipboardText(ev);
  if (text !== undefined) {
    return text;
  }
  const el = eventTarget<HTMLInputElement>(ev);
  if (el.value) {
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start !== null && end !== null) {
      return el.value.substring(start, end);
    }
  }
  return null;
}

function parseJson(s?: string): unknown {
  try {
    return s ? JSON.parse(s) : null;
  } catch (_) {
    return null;
  }
}

interface SerializedCardData {
  text: string;
  typeId: string;
  typeName: string;
  almSourceId: AlmSourceId | null;
  pos: RelativeCoordinate;
}

function isSerializedCard(data: unknown): data is SerializedCardData {
  return (
    isNonPrimitive(data) &&
    "text" in data &&
    "typeId" in data &&
    "typeName" in data &&
    "almSourceId" in data &&
    "pos" in data
  );
}

function serializeCard(card: BoardCard): SerializedCardData {
  return {
    typeId: card.data.type.id,
    typeName: card.data.type.name,
    almSourceId: card.data.almSourceId,
    text: card.data.text,
    pos: card.meta.pos,
  };
}

function deserializeCard(
  base: RelativeCoordinate,
  data: SerializedCardData,
  newBase: RelativeCoordinate,
): CardCreateProps {
  const stickyTypes = useBoardStore().creatableStickyTypes;
  const relativeToBase = minus(windowFromRelative(data.pos), windowFromRelative(base));
  const adjustedPos = plus(windowFromRelative(newBase), relativeToBase);
  return {
    text: data.text,
    almSourceId: data.almSourceId ?? undefined,
    // cross board pasting -> try to match type name
    // if still not found, take the first sticky type
    // TODO improvement: ask the user how to map every sticky type
    type:
      stickyTypes.find((type) => type.id === data.typeId) ||
      stickyTypes.find((type) => type.name === data.typeName) ||
      stickyTypes[0],
    pos: viewportFromWindow(adjustedPos),
  };
}
